import React, { useState, useEffect, useCallback } from 'react';
import { devLog } from '../lib/log';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useDataCache } from '../contexts/DataCacheContext';
import { getActiveProgramWithWorkouts, getNextSuggestedWorkout, getProgramVolumeDetailed, getProgramDetails } from '../lib/database';
import { useSwipeBack } from '../hooks/useSwipeBack';
import SkeletonLoader from './SkeletonLoader';
import VolumeBreakdownSheet from './VolumeBreakdownSheet';
import './AthleteProgram.css';

function AthleteProgram() {
  const navigate = useNavigate();
  const { programId } = useParams();
  const { profile } = useAuth();
  const [program, setProgram] = useState(null);
  const [workouts, setWorkouts] = useState([]);
  const [suggestedWorkoutId, setSuggestedWorkoutId] = useState(null);
  const [expandedWorkouts, setExpandedWorkouts] = useState(new Set());
  const [programVolume, setProgramVolume] = useState({});
  const [volumeDetailed, setVolumeDetailed] = useState({});
  const [isVolumeExpanded, setIsVolumeExpanded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isViewMode, setIsViewMode] = useState(false);
  const [selectedMuscle, setSelectedMuscle] = useState(null);

  // PERFORMANCE: Use cache for program data
  const fetchProgramData = useCallback(async () => {
    const t0 = performance.now();

    if (programId) {
      // View mode - fetch specific program
      const data = await getProgramDetails(programId);
      /*#__PURE__*/ devLog('📋 [ATHLETE PROGRAM] Loaded in ', Math.round(performance.now() - t0), 'ms');
      return { data, isViewMode: true };
    } else {
      // Active program mode - fetch with parallel queries
      const data = await getActiveProgramWithWorkouts(profile.id);
      if (!data || !data.program) {
        return null;
      }

      // PERFORMANCE: Parallel fetch of suggested workout and volume (detailed for breakdown)
      const [suggestedResult, volumeDetailed] = await Promise.all([
        data.workouts?.length > 0
          ? getNextSuggestedWorkout(profile.id, data.program.id).catch(() => null)
          : Promise.resolve(null),
        getProgramVolumeDetailed(data.program.id).catch(() => ({}))
      ]);

      // Convert detailed volume to simple format for display
      const volume = {};
      Object.entries(volumeDetailed).forEach(([muscle, data]) => {
        volume[muscle] = data.total || 0;
      });

      /*#__PURE__*/ devLog('📋 [ATHLETE PROGRAM] All data loaded in ', Math.round(performance.now() - t0), 'ms');
      return {
        data,
        isViewMode: false,
        suggestedId: typeof suggestedResult === 'string' ? suggestedResult : suggestedResult?.suggestedId,
        volume,
        volumeDetailed
      };
    }
  }, [profile?.id, programId]);

  const cacheKey = programId
    ? `athlete-program-view-${programId}`
    : `athlete-program-active-${profile?.id}`;

  const { data: cachedData, loading: cacheLoading } = useDataCache(
    profile?.id ? cacheKey : null,
    fetchProgramData,
    60000 // 1 minute cache
  );

  // Update local state when cache data changes
  useEffect(() => {
    // Wait until loading is complete before checking for null
    if (cacheLoading) return;

    if (cachedData === null) {
      // No active program - redirect only after loading is complete
      navigate('/athlete');
      return;
    }
    if (cachedData) {
      setProgram(cachedData.data?.program || cachedData.data);
      setWorkouts(cachedData.data?.workouts || []);
      setIsViewMode(cachedData.isViewMode || false);
      if (cachedData.suggestedId) {
        setSuggestedWorkoutId(cachedData.suggestedId);
      }
      if (cachedData.volume) {
        setProgramVolume(cachedData.volume);
      }
      if (cachedData.volumeDetailed) {
        setVolumeDetailed(cachedData.volumeDetailed);
      }
      setLoading(false);
    }
  }, [cachedData, cacheLoading, navigate]);

  // Sync loading state with cache
  useEffect(() => {
    setLoading(cacheLoading && !cachedData);
  }, [cacheLoading, cachedData]);

  const handleBack = () => {
    if (isViewMode) {
      navigate('/athlete/programs');
    } else {
      navigate('/athlete');
    }
  };

  const { handleTouchStart, handleTouchEnd } = useSwipeBack(handleBack);

  const handleStartWorkout = (workoutId) => {
    navigate(`/athlete/workout/${workoutId}`);
  };

  const handleToggleExpand = (workoutId) => {
    setExpandedWorkouts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(workoutId)) {
        newSet.delete(workoutId);
      } else {
        newSet.add(workoutId);
      }
      return newSet;
    });
  };

  const getExercisePreview = (exercises) => {
    if (!exercises || exercises.length === 0) return 'Nessun esercizio';
    const preview = exercises.slice(0, 3).map(ex => ex.name).join(', ');
    const remaining = exercises.length - 3;
    return remaining > 0 ? `${preview} +${remaining}` : preview;
  };

  const volumeMuscles = [
    'Petto', 'Schiena', 'Spalle', 'Bicipiti', 'Tricipiti',
    'Quadricipiti', 'Femorali', 'Glutei', 'Core', 'Polpacci'
  ];

  const totalSets = Object.values(programVolume).reduce((sum, v) => sum + (v || 0), 0);
  const displayTotalSets = totalSets % 1 === 0 ? totalSets : totalSets.toFixed(1);

  if (loading) {
    return (
      <div className="ap-container">
        <header className="ap-header">
          <button className="ap-back" onClick={handleBack}>
            ←
          </button>
          <h1 className="ap-title">Caricamento...</h1>
        </header>
        <main className="ap-main">
          <SkeletonLoader variant="list" count={3} />
        </main>
      </div>
    );
  }

  if (!program) {
    return (
      <div className="ap-container">
        <div className="ap-empty">
          <p>Nessun programma attivo</p>
          <button className="ap-btn-back-home" onClick={handleBack}>Torna alla Dashboard</button>
        </div>
      </div>
    );
  }

  const suggestedWorkout = workouts.find(w => w.id === suggestedWorkoutId);

  return (
    <div
      className="ap-container"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* Sticky Header */}
      <header className="ap-header">
        <button className="ap-header-back" onClick={handleBack} aria-label="Indietro">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
        <div className="ap-header-text">
          <span className="ap-header-title">Scheda attiva</span>
          <span className="ap-header-subtitle">{program.name}</span>
        </div>
      </header>

      <main className="ap-main">
        {/* Suggested Workout - Compact */}
        {suggestedWorkout && (
          <div className="ap-suggested">
            <div className="ap-suggested-top">
              <div className="ap-suggested-info">
                <span className="ap-suggested-badge">PROSSIMO</span>
                <span className="ap-suggested-name">{suggestedWorkout.name}</span>
              </div>
              <span className="ap-suggested-day">G{suggestedWorkout.day_number}</span>
            </div>
            <p className="ap-suggested-preview">
              {getExercisePreview(suggestedWorkout.exercises)}
            </p>
            <button
              className="ap-btn-start-suggested"
              onClick={() => handleStartWorkout(suggestedWorkout.id)}
            >
              Inizia Allenamento
            </button>
          </div>
        )}

        {/* Volume Accordion */}
        <div className={`ap-volume ${isVolumeExpanded ? 'ap-volume--open' : ''}`}>
          <button
            className="ap-volume-header"
            onClick={() => setIsVolumeExpanded(!isVolumeExpanded)}
          >
            <span className="ap-volume-title">Volume Totale</span>
            <span className="ap-volume-summary">{displayTotalSets} serie</span>
            <span className={`ap-volume-chevron ${isVolumeExpanded ? 'ap-volume-chevron--open' : ''}`}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </span>
          </button>
          {isVolumeExpanded && (
            <div className="ap-volume-grid">
              {volumeMuscles.map(muscle => {
                const sets = programVolume[muscle] || 0;
                const displaySets = sets % 1 === 0 ? sets : sets.toFixed(1);
                const hasExercises = volumeDetailed[muscle]?.exercises?.length > 0;
                return (
                  <button
                    key={muscle}
                    className={`ap-volume-row ${hasExercises ? 'ap-volume-row--clickable' : ''}`}
                    onClick={() => hasExercises && setSelectedMuscle(muscle)}
                    disabled={!hasExercises}
                  >
                    <span className="ap-volume-muscle">{muscle}</span>
                    <span className="ap-volume-sets">
                      {displaySets} serie
                      {hasExercises && <span className="ap-volume-arrow">›</span>}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Workout List */}
        <div className="ap-workouts-section">
          <h2 className="ap-section-title">Allenamenti</h2>
          <div className="ap-workouts-list">
            {workouts.map(workout => {
              const isExpanded = expandedWorkouts.has(workout.id);
              const isSuggested = workout.id === suggestedWorkoutId;
              return (
                <div
                  key={workout.id}
                  className={`ap-workout-card ${isSuggested ? 'ap-workout-card--suggested' : ''}`}
                >
                  <div className="ap-workout-row" onClick={() => handleToggleExpand(workout.id)}>
                    <div className="ap-workout-left">
                      <span className="ap-workout-day">G{workout.day_number}</span>
                      <div className="ap-workout-info">
                        <span className="ap-workout-name">{workout.name}</span>
                        <span className="ap-workout-meta">
                          {workout.exercises?.length || 0} esercizi
                        </span>
                      </div>
                    </div>
                    <div className="ap-workout-right">
                      <span className={`ap-workout-chevron ${isExpanded ? 'ap-workout-chevron--open' : ''}`}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="6 9 12 15 18 9" />
                        </svg>
                      </span>
                    </div>
                  </div>

                  {!isExpanded && (
                    <p className="ap-workout-preview">
                      {getExercisePreview(workout.exercises)}
                    </p>
                  )}

                  {isExpanded && (
                    <div className="ap-workout-detail">
                      {workout.exercises && workout.exercises.length > 0 ? (
                        workout.exercises.map((exercise, idx) => (
                          <div key={idx} className="ap-exercise-row">
                            <span className="ap-exercise-name">{exercise.name}</span>
                            <span className="ap-exercise-spec">
                              {exercise.sets}×{exercise.reps}
                              {exercise.weight ? ` ${exercise.weight}kg` : ''}
                            </span>
                          </div>
                        ))
                      ) : (
                        <p className="ap-no-exercises">Nessun esercizio</p>
                      )}
                    </div>
                  )}

                  <button
                    className="ap-btn-start"
                    onClick={(e) => { e.stopPropagation(); handleStartWorkout(workout.id); }}
                  >
                    Inizia
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        {workouts.length === 0 && (
          <div className="ap-empty-workouts">
            <p className="ap-empty-title">Nessun workout</p>
            <p className="ap-empty-sub">Il tuo coach aggiunger&agrave; presto gli allenamenti</p>
          </div>
        )}
      </main>

      {/* Volume Breakdown Sheet */}
      {selectedMuscle && (
        <VolumeBreakdownSheet
          muscleGroup={selectedMuscle}
          volumeData={volumeDetailed}
          onClose={() => setSelectedMuscle(null)}
        />
      )}
    </div>
  );
}

export default AthleteProgram;
